// @flow
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, Controller } from 'react-hook-form';
import { isMobile } from 'react-device-detect';
import { isValidEmail } from 'utils/validators';
import { Title, SmallTitle } from 'components/shared/CustomizeGoalcardTitle/Title';
import selectorChecked from 'assets/images/selector-checked.png';
import selectorUnchecked from 'assets/images/selector-unchecked.png';
import ErrorLabel from 'components/shared/ErrorLabel/ErrorLabel';
import { sendAnalyticsEvent } from 'utils/analyticsWrapper';
import GoalcardUploadOption from '../GoalcardUploadOption/index';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

import {
  Container,
  OptionContainer,
  InputContainer,
  Input,
  Label,
  Checkbox,
  RadioContainer,
  RadioContainerMargin,
  TextboxInput,
  TextboxContainer,
  DatePickerInput,
  SelectorImage,
} from './Styles';

type GoacardCustomizationProps = {
  receiver: any,
  setInvestorCallback(investorName: string, investorEmail: string): void,
  setReceiverCallback(receiverName: string): void,
  setUploadedMediaCallback(uploadedMedia: string) : void
};

const GoacardCustomization = (props: GoacardCustomizationProps) => {
  const { t } = useTranslation();
  const [pageState, updatePageState]: [any, ((any => any) | any) => void] = useState({});
  const {
    receiver, setInvestorCallback, setReceiverCallback, setUploadedMediaCallback,
    receiverEmail, investorEmail, investorName,
    receiverName,
    isScheduled,
    scheduledDate,
    receiverType,
    description,
    uploadedMedia,
  } = props;

  const isGoalcardToNonUser = (receiver && receiver.rid === undefined);
  const {
    register, errors, control, watch,
  } = useFormContext(); // retrieve all hook methods

  useEffect(() => {
    const initState = {
      investorName: investorName || '',
      investorEmail: investorEmail || '',
      receiverEmail: receiverEmail || '',
      receiverName: receiverName || '',
      description,
      isScheduled,
      receiverType,
    };

    if (receiver && receiver.fullName) {
      if (isValidEmail(receiver.fullName)) {
        initState.receiverEmail = receiver.fullName;
      } else if (receiverName) {
        initState.receiverName = receiverName;
      } else {
        initState.receiverName = receiver.fullName;
      }
    }

    updatePageState(initState);
  }, []);

  useEffect(() => {
    setInvestorCallback(
      pageState.investorName,
      pageState.investorEmail,
    );
  }, [pageState.investorName, pageState.investorEmail]);

  useEffect(() => {
    setReceiverCallback(
      pageState.receiverName,
    );
  }, [pageState.receiverName]);

  const handleInputChange = (e) => {
    if (e.target.value === 'nonscheduled') {
      sendAnalyticsEvent('Customize Goalcard', 'customize_goalcard_set_nonscheduled');
    }
    if (e.target.value === 'scheduled') {
      sendAnalyticsEvent('Customize Goalcard', 'customize_goalcard_set_scheduled');
    }

    updatePageState({
      ...pageState,
      [e.target.name]: e.target.value,
    });
  };

  const DatePickerInputMobileReady = ({ ...props }) => (
    <input type="text" {...props} readOnly />
  );

  const RenderScheduledContainer = () => {
    if (pageState.isScheduled === 'nonscheduled') {
      return null;
    }

    const today = new Date();
    const tomorrow = new Date(today);
    const todayPlusTwoYears = new Date(today);

    tomorrow.setDate(tomorrow.getDate() + 1);
    todayPlusTwoYears.setDate(tomorrow.getDate() + 730);

    const initialValue = scheduledDate ? Date.parse(moment(scheduledDate).format('L')) : null;

    return (
      <OptionContainer>
        <Controller
          control={control}
          name="scheduledDate"
          defaultValue={initialValue}
          as={(
            <DatePickerInput
              minDate={tomorrow}
              maxDate={todayPlusTwoYears}
              dateFormat="MM/dd/yyyy"
              maxWidth="200"
              placeholderText="MM/DD/YYYY"
              withPortal={!!isMobile}
              selected={watch('scheduledDate')}
              autoComplete="off"
              customInput={<DatePickerInputMobileReady />}
            />
        )}
          rules={{ required: true }}
        />
        {errors.scheduledDate?.type === 'required' && <ErrorLabel text={t('customize_validation_date_title')} />}
      </OptionContainer>
    );
  };

  const RenderInvestorInputs = () => (
    <>
      <OptionContainer>
        <SmallTitle>{t('customize_options_investorname_title')}</SmallTitle>
        <InputContainer>
          <Input
            data-testid="inputInvestorName"
            name="investorName"
            onChange={handleInputChange}
            value={pageState.investorName}
            placeholder={t('customize_options_investorname_placeholder')}
            maxLength="100"
            ref={register({ required: true })}
          />
        </InputContainer>
        {errors.investorName?.type === 'required' && <ErrorLabel data-testid="lblErrorInvestorName" text={t('customize_validation_investorname_title')} />}
      </OptionContainer>
      <OptionContainer>
        <SmallTitle>{t('customize_options_investoremail_title')}</SmallTitle>
        <InputContainer>
          <Input
            data-testid="inputInvestorEmail"
            name="investorEmail"
            value={pageState.investorEmail}
            onChange={handleInputChange}
            placeholder={t('customize_options_investoremail_placeholder')}
            maxLength="100"
            ref={register({ required: true, validate: { format: isValidEmail } })}
          />
        </InputContainer>
        {errors.investorEmail?.type === 'required' && <ErrorLabel data-testid="lblErrorInvestorEmail" text={t('customize_validation_investoremail_notcompleted_title')} />}
        {errors.investorEmail?.type === 'format' && <ErrorLabel text={t('customize_validation_investoremail_notvalid_title')} />}
      </OptionContainer>
    </>
  );

  const RenderReceiverInformation = () => {
    if (isGoalcardToNonUser) {
      return (
        <>
          <OptionContainer>
            <SmallTitle>{t('customize_options_receivername_title')}</SmallTitle>
            <InputContainer>
              <Input
                data-testid="inputReceiverName"
                name="receiverName"
                value={pageState.receiverName}
                onChange={handleInputChange}
                placeholder={t('customize_options_receivername_placeholder')}
                maxLength="100"
                ref={register({ required: true })}
              />
            </InputContainer>
            {errors.receiverName?.type === 'required' && <ErrorLabel data-testid="lblErrorRecipientName" text={t('customize_validation_receivername_title')} />}
          </OptionContainer>
          <OptionContainer>
            <SmallTitle>{t('customize_options_receiveremail_title')}</SmallTitle>
            <InputContainer>
              <Input
                data-testid="inputReceiverEmail"
                name="receiverEmail"
                value={pageState.receiverEmail}
                onChange={handleInputChange}
                placeholder={t('customize_options_receiveremail_placeholder')}
                maxLength="100"
                ref={register({ required: true, validate: { format: isValidEmail } })}
              />
            </InputContainer>
            {errors.receiverEmail?.type === 'required' && <ErrorLabel data-testid="lblErrorReceipentEmail" text={t('customize_validation_receiveremail_notcompleted_title')} />}
            {errors.receiverEmail?.type === 'format' && <ErrorLabel text={t('customize_validation_receiveremail_notvalid_title')} />}
          </OptionContainer>
          <RadioContainerMargin>
            <Label>
              <SelectorImage src={pageState.receiverType === 'parent' ? selectorChecked : selectorUnchecked} alt="selector" />
              <Checkbox
                data-testid="rbParentEmail"
                type="radio"
                name="receiverType"
                value="parent"
                onChange={handleInputChange}
                checked={pageState.receiverType === 'parent'}
                ref={register({ required: true })}
              />
              {t('customize_options_receivertypeparent_title')}
            </Label>
            <Label>
              <SelectorImage src={pageState.receiverType === 'kid' ? selectorChecked : selectorUnchecked} alt="selector" />
              <Checkbox
                data-testid="rbKidEmail"
                type="radio"
                name="receiverType"
                value="kid"
                onChange={handleInputChange}
                checked={pageState.receiverType === 'kid'}
                ref={register({ required: true })}
              />
              {t('customize_options_receivertypekid_title')}
            </Label>
          </RadioContainerMargin>
        </>
      );
    }
    return null;
  };

  return (
    <Container>
      <Title>{t('customize_options_title')}</Title>
      {RenderReceiverInformation()}

      { isGoalcardToNonUser === false && RenderInvestorInputs()}

      <OptionContainer>
        <SmallTitle>{t('customize_options_when_title')}</SmallTitle>
        <RadioContainer>
          <Label>
            <SelectorImage src={pageState.isScheduled === 'nonscheduled' ? selectorChecked : selectorUnchecked} alt="selector" />
            <Checkbox
              data-testid="rbNow"
              type="radio"
              name="isScheduled"
              value="nonscheduled"
              onChange={handleInputChange}
              checked={pageState.isScheduled === 'nonscheduled'}
            />
            {t('customize_options_now_title')}
          </Label>
          <Label>
            <SelectorImage src={pageState.isScheduled === 'scheduled' ? selectorChecked : selectorUnchecked} alt="selector" />
            <Checkbox
              data-testid="rbScheduled"
              type="radio"
              name="isScheduled"
              value="scheduled"
              onChange={handleInputChange}
              checked={pageState.isScheduled === 'scheduled'}
            />
            {t('customize_options_specific_title')}
          </Label>
        </RadioContainer>
      </OptionContainer>
      {RenderScheduledContainer()}
      <OptionContainer>
        <SmallTitle>{t('customize_options_message_title')}</SmallTitle>
        <TextboxContainer>
          <TextboxInput
            name="description"
            maxLength="500"
            onChange={handleInputChange}
            ref={register({ required: false })}
            value={pageState.description}
            placeholder={t('customize_options_message_placeholder')}
          />
        </TextboxContainer>
        <GoalcardUploadOption uploadedMedia={uploadedMedia} setUploadedMediaCallback={setUploadedMediaCallback} />
      </OptionContainer>

      { isGoalcardToNonUser && RenderInvestorInputs()}
    </Container>
  );
};

export default GoacardCustomization;
